import { Icon } from "./icons";
import { detailHref } from "./links";
import type { SecurityBoard, SecuritySite } from "./model";

const SEVERITY_TONE = {
  critical: "down",
  high: "down",
  medium: "degraded",
  low: "unknown",
  info: "unknown",
  CRITICAL: "down",
  HIGH: "down",
  MODERATE: "degraded",
  LOW: "unknown",
  UNKNOWN: "unknown",
} as const;

/**
 * The security view: each site's web scan (grade, what it lacks or exposes) and what it is
 * built from that needs updating (libraries with known vulnerabilities, runtimes out of
 * support), worst first. Scanned once a day; a fix shows up on the next scan.
 */
export function SecurityView({ board, stale }: { board: SecurityBoard | null; stale: boolean }) {
  if (!board) {
    return (
      <main className="view">
        <p className="muted">
          Nothing is scanned: enable <code>security</code> in the http module and{" "}
          <code>dependencies</code> in the github module.
        </p>
      </main>
    );
  }
  return (
    <main className="view" data-stale={stale}>
      <ul className="tally" aria-label="Security at a glance">
        <li className="tally-item" data-status={board.critical > 0 ? "down" : "up"}>
          <Icon name={board.critical > 0 ? "down" : "up"} />
          <b className="num">{board.critical}</b>
          <span>critical vulnerabilities in libraries</span>
        </li>
        <li className="tally-item" data-status={board.high > 0 ? "down" : "up"}>
          <Icon name={board.high > 0 ? "down" : "up"} />
          <b className="num">{board.high}</b>
          <span>high vulnerabilities</span>
        </li>
        <li className="tally-item" data-status={board.outOfSupport.length > 0 ? "degraded" : "up"}>
          <Icon name={board.outOfSupport.length > 0 ? "degraded" : "up"} />
          <b className="num">{board.outOfSupport.length}</b>
          <span>runtimes out of support</span>
          {board.outOfSupport.length > 0 && (
            <span className="tally-names">{board.outOfSupport.join(", ")}</span>
          )}
        </li>
        <li className="tally-item" data-status={board.poorGrades.length > 0 ? "degraded" : "up"}>
          <Icon name={board.poorGrades.length > 0 ? "degraded" : "up"} />
          <b className="num">{board.poorGrades.length}</b>
          <span>sites graded D or F</span>
          {board.poorGrades.length > 0 && (
            <span className="tally-names">
              {board.poorGrades.map((t, i) => (
                <span key={t}>
                  {i > 0 && ", "}
                  <a href={detailHref(t)}>{t}</a>
                </span>
              ))}
            </span>
          )}
        </li>
      </ul>

      <section className="panel" aria-labelledby="security-title">
        <div className="panel-head">
          <h2 id="security-title" className="eyebrow">
            Sites
          </h2>
          <span className="num">worst first · scanned daily</span>
        </div>
        <ul className="security">
          {board.sites.map((site) => (
            <SiteSecurity key={site.target} site={site} />
          ))}
        </ul>
      </section>
    </main>
  );
}

function SiteSecurity({ site }: { site: SecuritySite }) {
  const { web, deps } = site;
  return (
    <li className="security-site" data-status={site.status}>
      <details>
        <summary>
          <Icon name={site.status} />
          <a className="errors-name" href={detailHref(site.target)}>
            {site.target}
          </a>
          {web && (
            <span className="grade" data-grade={web.grade} title={web.message}>
              {web.grade}
            </span>
          )}
          <span className="security-summary muted">
            {[web?.message, deps?.message].filter(Boolean).join(" · ")}
          </span>
        </summary>
        <div className="security-body">
          {web && web.findings.length > 0 && (
            <section>
              <h3 className="eyebrow">Web</h3>
              <ul className="security-list">
                {web.findings.map((f) => (
                  <li key={`${f.origin}/${f.code}`} data-status={SEVERITY_TONE[f.severity]}>
                    <span className="security-sev">{f.severity}</span>
                    <span>
                      {f.evidence}{" "}
                      <span className="muted">({f.origin.replace(/^https?:\/\//, "")})</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {deps && deps.runtimes.length > 0 && (
            <section>
              <h3 className="eyebrow">Runtimes</h3>
              <ul className="security-list">
                {deps.runtimes.map((r) => (
                  <li
                    key={`${r.product}${r.cycle}`}
                    data-status={r.state === "eol" ? "down" : "degraded"}
                  >
                    <span className="security-sev">
                      {r.state === "eol" ? "out of support" : "ends soon"}
                    </span>
                    <span>
                      <b>
                        {r.product} {r.cycle}
                      </b>{" "}
                      {r.state === "eol" ? "unsupported" : "supported"}{" "}
                      {r.eol !== "yes" && `${r.state === "eol" ? "since" : "until"} ${r.eol}`}{" "}
                      <span className="muted mono">{r.image}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {deps && deps.vulnerabilities.length > 0 && (
            <section>
              <h3 className="eyebrow">Libraries</h3>
              <ul className="security-list">
                {deps.vulnerabilities.map((v) => (
                  <li key={`${v.id}/${v.package}`} data-status={SEVERITY_TONE[v.severity]}>
                    <span className="security-sev">{v.severity.toLowerCase()}</span>
                    <span>
                      <b className="mono">
                        {v.package} {v.version}
                      </b>
                      {v.fixed && <> → update to {v.fixed}</>}: {v.summary || v.id}{" "}
                      <a
                        href={`https://osv.dev/vulnerability/${encodeURIComponent(v.id)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {v.id}
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
              {deps.more > 0 && <p className="muted">And {deps.more} more.</p>}
            </section>
          )}
          {(!web || web.findings.length === 0) &&
            (!deps || (deps.runtimes.length === 0 && deps.vulnerabilities.length === 0)) && (
              <p className="muted">Nothing to fix.</p>
            )}
        </div>
      </details>
    </li>
  );
}
